import { Inject, Injectable } from '@angular/core';
import { filter, map as rxMap, Observable } from 'rxjs';

import {
  DynamicFormExpressions,
  DynamicFormsExtensionsService,
} from '@lockton-apps/dynamic-forms';
import { PROPOSAL_PARTNER_DATA } from '../../constants/proposal-partner-data';
import { map, sum, zipWith } from 'lodash';
import { ProposalProductDetailsComponent } from '@lockton-apps/shared/ui/proposal-product-details';
import { CastObservablePipe } from '@lockton-apps/shared/ui/pipes';
import {
  DocumentsService,
  SupportingDocumentsService,
} from '@lockton-apps/shared/data-access/documents';
import { FeedbackService } from '@lockton-apps/shared/ui/lui';
import { MatDialog } from '@angular/material/dialog';

interface PartnerData {
  proposalId: string | undefined;
  createdDate: string;
  availableAddonCobs: number[];
  readonly: boolean;
}

@Injectable()
export class ConstructionDynamicFormsExtensionsService extends DynamicFormsExtensionsService {
  constructor(
    @Inject(PROPOSAL_PARTNER_DATA)
    protected readonly partnerData$: Observable<PartnerData>,
    castObservable: CastObservablePipe,
    documentsService: DocumentsService,
    supportingDocumentsService: SupportingDocumentsService,
    feedbackService: FeedbackService,
    dialog: MatDialog
  ) {
    super(
      castObservable,
      documentsService,
      supportingDocumentsService,
      feedbackService,
      dialog
    );
  }

  override readonly expressions: Record<string, DynamicFormExpressions> = {
    percentageOfIncomeDerived: {
      'options.formState.derivedValues.totals': (field) => {
        const value: number[][] = map(
          field.props?.['partials'],
          (partial) =>
            field.options?.formState.derivedValues[
              `activities.distribution.${partial}.totals`
            ]
        );

        return zipWith(...value, (...partials) => sum(partials));
      },
    },
  };

  override readonly extensions = [
    {
      id: 'thirdPartyResponsibility',
      prop: 'displayWith',
      value: (value: boolean) =>
        value ? $localize`Yes` : $localize`Appointed direct by client`,
    },
    {
      id: 'productDetails',
      prop: 'component',
      value: ProposalProductDetailsComponent,
    },
    {
      id: 'productDetailsInputs',
      prop: 'inputs',
      value: '',
      mappedValue: () => ({
        cobs: this.partnerData$.pipe(
          filter(Boolean),
          rxMap(({ availableAddonCobs }) => availableAddonCobs)
        ),
      }),
    },
  ];
}
